import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import { jwtDecode } from 'jwt-decode';
import { Button } from 'react-bootstrap';
import { useLoading } from '../../context/LoadingContext';
import config from '../../aws-exports';
import '../../styles/general.css';
import '../../styles/my-farmers.css';

const MyFarmersDashboard = () => {
  const [activeTab, setActiveTab] = useState('basicInfo');
  const [farmers, setFarmers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [names, setNames] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [phones, setPhones] = useState([]);
  const [filterTerms, setFilterTerms] = useState({ name: '', address: '', phone: '' });
  const [farmerChanges, setFarmerChanges] = useState({});
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const navigate = useNavigate();
  const pageSize = 10;
  const { setLoading } = useLoading();

  const getBackendName = () => {
    const backend = config.aws_user_files_s3_bucket.split('-');
    return backend[backend.length - 1];
  };
  
  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        // Check if the user is a member of the AMTPL_AGROPRENEURS group
        const groups = decodedToken['cognito:groups'] || [];
        if (!groups.includes('AMTPL_AGROPRENEURS')) {
          await Auth.signOut(); // Sign out the user
          navigate('/login/agropreneur');
        }
      } catch (error) {
        await Auth.signOut(); // Sign out the user
        navigate('/login/agropreneur');
      }
    };

    const fetchFarmers = async () => {
      setLoading(true);
      try {
        const credentials = await Auth.currentCredentials();
        const lambdaClient = new LambdaClient({
          credentials: Auth.essentialCredentials(credentials),
          region: config.aws_project_region
        });
        const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
        const updatedFilterTerms = {
          ...filterTerms,
          GPEmailID: user.attributes.email
        };

        const body = {
          target: 'listFarmers',
          access_token: user.signInUserSession.idToken.jwtToken,
          filterTerms: updatedFilterTerms,
          rowStart: (currentPage - 1) * pageSize,
          pageSize: pageSize
        };

        const params = {
          FunctionName: `dataops-${getBackendName()}`,
          Payload: JSON.stringify({ body })
        };

        const command = new InvokeCommand(params);
        const data = await lambdaClient.send(command);
        let result = JSON.parse(new TextDecoder('utf-8').decode(data.Payload));
        if (result.statusCode === 200) {
          result = JSON.parse(result.body);
          setFarmers(result.items);
          setTotalPages(Math.ceil(result.total / pageSize));
          if (result.items.length > 0) {
            setSelectedFarmer(result.items[0]);
            setFarmerChanges(result.items[0]);
          }
        } else {
          console.error('Error fetching farmers: ', result);
        }
      } catch (error) {
        console.error('Error invoking Lambda function: ', error);
      } finally {
        setLoading(false);
      }
    };

    checkUser();
    fetchFarmers();
    fetchSearchParams('farmers','names');
    fetchSearchParams('farmers','addresses');
    fetchSearchParams('farmers','phones');
  }, [navigate, currentPage, setLoading, filterTerms]);

  const handleSignOut = async () => {
    setLoading(true);
    try {
      await Auth.signOut();
      navigate('/login/agropreneur');
    } catch (error) {
      console.error('Error signing out: ', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRowClick = (farmer) => {
    setSelectedFarmer(farmer);
    setFarmerChanges(farmer);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterTerms((prevTerms) => ({ ...prevTerms, [name]: value }));
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleReset = () => {
    setFilterTerms({ name: '', address: '', phone: '' });
    resetFilter();
  };

  const resetFilter = () => {
    setSelectedFarmer(null);
    setCurrentPage(1);
    setFarmerChanges({});
    setIsSaveEnabled(false);    
  };

  const fetchSearchParams = async (option, suboption) => {
    setLoading(true);
    try {
      const credentials = await Auth.currentCredentials();
      const lambdaClient = new LambdaClient({
        credentials: Auth.essentialCredentials(credentials),
        region: config.aws_project_region
      });
  
      const user = await Auth.currentAuthenticatedUser();
      const token = user.signInUserSession.idToken.jwtToken;
      const backendName = getBackendName();

      const updatedFilterTerms = {
        ...filterTerms,
        GPEmailID: user.attributes.email
      };
  
      const command = new InvokeCommand({
        FunctionName: `dataops-${backendName}`,
        Payload: JSON.stringify({
          body: {
            target: 'listDynamicDropDownOptions',
            userTable: 'AMTPLUsers',
            option: option,
            suboption: suboption,
            filterTerms: updatedFilterTerms,
            access_token: token,
            rowStart: 0,
            pageSize: 100 
          }
        })
      });
  
      const data = await lambdaClient.send(command);
      let result = JSON.parse(new TextDecoder('utf-8').decode(data.Payload));
      if (result.statusCode === 200) {
        result = JSON.parse(result.body);
        if(suboption === 'names') {
          setNames(result.items.map(option => option.result));
        } else if(suboption === 'addresses') {
          setAddresses(result.items.map(option => option.result));
        } else if(suboption === 'phones') {
          setPhones(result.items.map(option => option.result));
        }
      } else {
        console.error('Error fetching products: ', result);
      }
    } catch (error) {
      console.error('Error fetching products', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFarmerChanges((prevChanges) => ({ ...prevChanges, [name]: value }));
    setIsSaveEnabled(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const credentials = await Auth.currentCredentials();
      const lambdaClient = new LambdaClient({
        credentials: Auth.essentialCredentials(credentials),
        region: config.aws_project_region
      });
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
  
      const body = {
        target: 'updateFarmer',
        access_token: user.signInUserSession.idToken.jwtToken,
        updateFarmerInput: farmerChanges
      };
  
      const params = {
        FunctionName: `dataops-${getBackendName()}`,
        Payload: JSON.stringify({ body })
      };
  
      const command = new InvokeCommand(params);
      const data = await lambdaClient.send(command);
      let result = JSON.parse(new TextDecoder('utf-8').decode(data.Payload));
      if (result.statusCode === 200) {
        setFarmers((prevFarmers) =>
          prevFarmers.map((farmer) =>
            farmer.FarmerID === farmerChanges.FarmerID ? farmerChanges : farmer
          )
        );
        setSelectedFarmer(farmerChanges);
        setFarmerChanges(farmerChanges);
        setIsSaveEnabled(false);
      } else {
        console.error('Error updating farmer: ', result);
      }
    } catch (error) {
      console.error('Error invoking Lambda function: ', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="my-farmers-dashboard">
      <header>
        <img src="/images/logo.png" alt="Grains Global Logo" className="logo" />
        <nav>
          <ul>
            <li><a href="/agropreneur">Dashboard</a></li>
            <li><a href="/my-farmers">My Farmers</a></li>
            <li><button onClick={handleSignOut} className="sign-out-button">Sign Out</button></li>
          </ul>
        </nav>
      </header>

      <div className="content">
        <main className="main-content">
          <div className='offers-search row'>
            <div className='col-lg-3 col-sm-4 col-6'>
              <select className="form-control form-select" id="name" name="name" value={filterTerms.name || ''} onChange={handleFilterChange}>
                <option value="">Select Name</option>
                {names.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div className='col-lg-3 col-sm-4 col-6'>
              <select className="form-control form-select" id="address" name="address" value={filterTerms.address || ''} onChange={handleFilterChange}>
                <option value="">Select Company</option>
                {addresses.map((address) => (
                  <option key={address} value={address}>{address}</option>
                ))}
              </select>
            </div>
            <div className='col-lg-3 col-sm-4 col-6'>
              <select className="form-control form-select" id="pin" name="pin" value={filterTerms.pin || ''} onChange={handleFilterChange}>
                <option value="">Select PIN/ZIP</option>
                {phones.map((phone) => (
                  <option key={phone} value={phone}>{phone}</option>
                ))}
              </select>
            </div>
            <div className='col-lg-3 col-sm-4 col-6'>
              <button onClick={handleSearch} className='mx-3 d-inline'>Search</button>
              <button onClick={handleReset} className='d-inline'>Reset</button>
            </div>
          </div>
          <div className="farmers-search">
            <input
              type="text"
              name="name"
              placeholder="Name"
              className="form-control"
              value={filterTerms.name}
              onChange={handleFilterChange}
            />
            <input
              type="text"
              name="address"
              placeholder="Address"
              className="form-control"
              value={filterTerms.address}
              onChange={handleFilterChange}
            />
            <input
              type="text"
              name="phone"
              placeholder="Phone"
              className="form-control"
              value={filterTerms.phone}
              onChange={handleFilterChange}
            />
            <div className="button-container">
              <Button variant="primary" onClick={handleSearch}>Search</Button>
              <Button variant="primary" onClick={handleReset}>Reset</Button>
            </div>
          </div>

          <section className="farmers">
            <table>
              <thead>
                <tr>
                  <th>Farmer ID</th>
                  <th>Farmer Name</th>
                  <th>Age</th>
                  <th>Gender</th>
                  <th>Address</th>
                  <th>Phone</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {farmers.map((farmer) => (
                  <tr
                    key={farmer.FarmerID}
                    onClick={() => handleRowClick(farmer)}
                    className={selectedFarmer && selectedFarmer?.FarmerID === farmer.FarmerID ? 'selected' : ''}
                  >
                    <td>{farmer.FarmerID}</td>
                    <td>{farmer.FarmerName}</td>
                    <td>{farmer.Age}</td>
                    <td>{farmer.Gender}</td>
                    <td>{farmer.Address}</td>
                    <td>{farmer.Phone}</td>
                    <td>{farmer.Email}</td>
                  </tr>
                ))}
                {farmers.length === 0 && (
                  <tr>
                    <td colSpan="7">No farmers available</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="pagination">
              <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>{'<'}</button>
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={currentPage === index + 1 ? 'active' : ''}
                >
                  {index + 1}
                </button>
              ))}
              <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>{'>'}</button>
            </div>
          </section>

          <section className="farmer-details">
            <div className="tabs">
              <button
                className={activeTab === 'basicInfo' ? 'active' : ''}
                onClick={() => handleTabClick('basicInfo')}
              >
                Basic Info
              </button>
              <button
                className={activeTab === 'history' ? 'active' : ''}
                onClick={() => handleTabClick('history')}
              >
                History
              </button>
            </div>

            {activeTab === 'basicInfo' && (
              <div className="tab-content active">
                <div className='row'>
                  <div className='col-6'>
                    <label>Farmer Name</label>
                    <input type="text" name="FarmerName" value={farmerChanges.FarmerName} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-3'>
                    <label>Age</label>
                    <input type="text" name="Age" value={farmerChanges.Age} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-3'>
                    <label>Gender</label>
                    <input type="text" name="Gender" value={farmerChanges.Gender} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Address 1</label>
                    <input type="text" name="Address1" value={farmerChanges.Address1} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Address 2</label>
                    <input type="text" name="Address2" value={farmerChanges.Address2} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Phone</label>
                    <input type="text" name="Phone" value={farmerChanges.Phone} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Email</label>
                    <input type="text" name="Email" value={farmerChanges.Email} onChange={handleInputChange} className='form-control' />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="tab-content active">
                <div className='row'>
                  <div className='col-3'>
                    <label>Year</label>
                    <input type="text" name="Year" value={farmerChanges.Year} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-3'>
                    <label>Month</label>
                    <input type="text" name="Month" value={farmerChanges.Month} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-3'>
                    <label>Crop</label>
                    <input type="text" name="Crop" value={farmerChanges.Crop} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-3'>
                    <label>Harvest Quantity</label>
                    <input type="text" name="HarvestQuantity" value={farmerChanges.HarvestQuantity} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-3'>
                    <label>Price / MT</label>
                    <input type="text" name="PricePerMT" value={farmerChanges.PricePerMT} onChange={handleInputChange} className='form-control' />
                  </div>
                </div>
                <div className='history-table'>
                  <table>
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Month</th>
                        <th>Crop</th>
                        <th>Quantity</th>
                        <th>Price/MT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedFarmer?.history.map((record, index) => (
                        <tr key={index}>
                          <td>{record.Year}</td>
                          <td>{record.Month}</td>
                          <td>{record.Crop}</td>
                          <td>{record.Quantity}</td>
                          <td>{record.PricePerMT}</td>
                        </tr>
                      ))}
                      {selectedFarmer?.history.length === 0 && (
                        <tr>
                          <td colSpan="5">No history available</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="actions mt-2 pb-3">
              <button onClick={handleSave} disabled={!isSaveEnabled}>Save</button>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default MyFarmersDashboard;
